import {
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { createPortal } from "react-dom";
import { useLingui } from "@lingui/react";
import { Button, cn } from "@wootingkb/component-library";
import type { Country, CountrySelectProps } from "./types";
import { flagEmoji } from "./utils";
import { ChevronIcon } from "./ChevronIcon";

/** CountrySelect is rendered inside the Input's leftIcon slot. */
export function CountrySelect({
  country,
  countries,
  disabled,
  renderFlag,
  onSelect,
}: CountrySelectProps) {
  const { _: i18n } = useLingui();
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [rect, setRect] = useState<DOMRect | null>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const listboxId = useId();

  const flag = (c: Country): ReactNode =>
    renderFlag ? renderFlag(c) : <span aria-hidden>{flagEmoji(c.iso2)}</span>;

  // Keep the portal positioned under the trigger (fixed coords, updates on scroll/resize).
  useEffect(() => {
    if (!open) return;
    const update = () =>
      setRect(triggerRef.current?.getBoundingClientRect() ?? null);
    update();
    window.addEventListener("scroll", update, true);
    window.addEventListener("resize", update);
    return () => {
      window.removeEventListener("scroll", update, true);
      window.removeEventListener("resize", update);
    };
  }, [open]);

  // Close on outside click / Escape.
  useEffect(() => {
    if (!open) return;
    const onPointer = (e: MouseEvent) => {
      const target = e.target as Node;
      if (
        triggerRef.current?.contains(target) ||
        menuRef.current?.contains(target)
      )
        return;
      setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onPointer);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onPointer);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return countries;
    return countries.filter(
      (c) =>
        i18n(c.name).toLowerCase().includes(q) ||
        c.iso2.toLowerCase().includes(q) ||
        c.dialCode.includes(q)
    );
  }, [countries, query, i18n]);

  return (
    <>
      <Button
        ref={triggerRef}
        type="button"
        variant="text"
        isDisabled={disabled}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={open ? listboxId : undefined}
        // stopPropagation: the Input wraps leftIcon in a span whose onClick
        // focuses the text field. Without this, clicking the selector would
        // focus the input instead of opening the dropdown.
        onClick={(e) => {
          e.stopPropagation();
          if (!disabled) setOpen((v) => !v);
        }}
        // Neutralize the WCL Button defaults (size=md brings px-5/py-4/min-h-52,
        // and the base adds a full transparent border + focus ring) so it sits
        // flush inside the input like the bare selector did.
        leafContainerClassName="gap-1.5 px-0"
        className={cn(
          "h-full min-h-0 rounded-none py-1.5 pr-2.5 pl-2.5 text-sm text-inherit",
          "border-0 border-r border-current/15",
          "focus-visible:ring-0 focus-visible:ring-offset-0",
          "enabled:hover:bg-transparent",
          "disabled:cursor-not-allowed disabled:bg-transparent"
        )}
      >
        <span className="text-base leading-none">{flag(country)}</span>
        <span className="tabular-nums">{country.dialCode}</span>
        <ChevronIcon
          className={cn("size-3 transition-transform", open && "rotate-180")}
        />
      </Button>

      {open &&
        rect &&
        createPortal(
          <div
            ref={menuRef}
            id={listboxId}
            role="listbox"
            style={{
              position: "fixed",
              top: rect.bottom + 4,
              left: rect.left,
              minWidth: Math.max(rect.width, 260),
            }}
            className={cn(
              "z-50 max-h-72 overflow-auto rounded-md border p-1 shadow-lg",
              "border-black/10 bg-white text-neutral-900",
              "dark:border-white/10 dark:bg-neutral-900 dark:text-neutral-100"
            )}
            onClick={(e) => e.stopPropagation()}
          >
            <input
              autoFocus
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search country or code"
              className="mb-1 w-full rounded bg-black/5 px-2 py-1.5 text-sm outline-none dark:bg-white/10"
            />

            {filtered.length === 0 ? (
              <div className="px-2 py-3 text-center text-sm opacity-60">
                No matches
              </div>
            ) : (
              filtered.map((c) => {
                const selected = c.iso2 === country.iso2;
                return (
                  <Button
                    key={c.iso2}
                    type="button"
                    variant="text"
                    role="option"
                    aria-selected={selected}
                    onClick={() => {
                      onSelect(c);
                      setOpen(false);
                      setQuery("");
                    }}
                    // Override the Button base's `justify-center` on BOTH the
                    // outer element and the leaf, force the leaf full-width, and
                    // hide the always-present (but here unused) spinner span so
                    // it doesn't take up horizontal space. min-h-0 keeps rows
                    // compact against the md size default.
                    leafContainerClassName="w-full justify-start gap-2 px-0 text-left"
                    className={cn(
                      "flex min-h-0 w-full justify-start rounded px-2 py-1.5 text-left text-sm [&>span:last-child]:hidden",
                      "hover:bg-black/5 dark:hover:bg-white/10",
                      selected && "bg-black/5 dark:bg-white/10"
                    )}
                  >
                    <span className="text-base leading-none">{flag(c)}</span>
                    <span className="flex-1 truncate">{i18n(c.name)}</span>
                    <span className="tabular-nums opacity-60">
                      {c.dialCode}
                    </span>
                  </Button>
                );
              })
            )}
          </div>,
          document.body
        )}
    </>
  );
}
